/** @jsxImportSource react */
import { useEffect, useRef } from "react";
import * as d3 from "d3";
import type { MesaWithComparison } from "../lib/types";

interface DispersionChartProps {
  data: MesaWithComparison[]; // Todas las mesas con party comparison
  filteredIds: Set<string>; // IDs de las mesas filtradas/visibles
  llaAverage: number; // Mediana o promedio de LLA (según selección)
  fpAverage: number; // Mediana o promedio de FP (según selección)
}

interface ScatterDataPoint {
  id: string;
  fp: number;
  lla: number;
  totalVotes: number;
  distrito: string;
  circuito: string;
  mesaNumber: string;
  isVisible: boolean;
}

export function DispersionChart({
  data,
  filteredIds,
  llaAverage,
  fpAverage,
}: DispersionChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || data.length === 0) return;

    // Obtener dimensiones del contenedor
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    // Dimensiones del chart
    const width = containerWidth;
    const height = containerHeight;
    const marginTop = 30;
    const marginRight = 30;
    const marginBottom = 50;
    const marginLeft = 60;

    // Filtrar datos válidos con partyComparison (usamos todas las mesas, no solo filteredIds)
    const validData = data.filter((d) => d.partyComparison);

    // Preparar datos para el scatter plot - incluir info de si está filtrada
    const scatterData = validData.map((mesa) => ({
      id: mesa.id,
      fp: mesa.partyComparison!.fuerzaPatria2025vs2023,
      lla: mesa.partyComparison!.lla2025vsJxC_LLA2023,
      distrito: mesa.data.distrito || "Sin distrito",
      circuito: mesa.data.circuito || "Sin circuito",
      mesaNumber: mesa.data.mesaNumber || "???",
      totalVotes: mesa.data.totalVotes || 300, // Default aprox si no hay datos
      isVisible: filteredIds.has(String(mesa.id)), // Nueva propiedad para saber si está visible
    }));

    // Calcular escala de tamaño basada en votos (siempre con TODOS los datos)
    const votesExtent = d3.extent(scatterData, (d) => d.totalVotes) as [
      number,
      number,
    ];
    const radiusScale = d3.scaleSqrt().domain(votesExtent).range([3, 8]); // Radio mínimo 3, máximo 8 (doble del anterior máximo de 4)

    // Calcular el dominio para incluir el 0 y ser simétrico
    const fpExtent = d3.extent(scatterData, (d) => d.fp) as [number, number];
    const llaExtent = d3.extent(scatterData, (d) => d.lla) as [number, number];

    const maxAbsFP = Math.max(Math.abs(fpExtent[0]), Math.abs(fpExtent[1]));
    const maxAbsLLA = Math.max(Math.abs(llaExtent[0]), Math.abs(llaExtent[1]));

    // Hacer el dominio simétrico alrededor del 0
    const fpDomain: [number, number] = [-maxAbsFP * 1.1, maxAbsFP * 1.1];
    const llaDomain: [number, number] = [-maxAbsLLA * 1.1, maxAbsLLA * 1.1];

    // Escalas
    const x = d3
      .scaleLinear()
      .domain(fpDomain)
      .nice()
      .range([marginLeft, width - marginRight]);

    const y = d3
      .scaleLinear()
      .domain(llaDomain)
      .nice()
      .range([height - marginBottom, marginTop]);

    // Seleccionar el SVG
    const svg = d3.select(svgRef.current);

    // Configurar viewBox para responsive (solo una vez)
    svg
      .attr("viewBox", [0, 0, width, height])
      .attr("style", "max-width: 100%; height: 100%; font: 10px sans-serif;");

    // Crear o actualizar elementos estáticos (solo si no existen)
    let staticGroup = svg.select<SVGGElement>("g.static-elements");
    if (staticGroup.empty()) {
      staticGroup = svg.append("g").attr("class", "static-elements");

      // Líneas de referencia en 0 (ejes centrales) - sin grid
      staticGroup
        .append("line")
        .attr("class", "vertical-reference")
        .attr("stroke", "#777")
        .attr("stroke-width", 1.5)
        .attr("stroke-opacity", 0.5);

      staticGroup
        .append("line")
        .attr("class", "horizontal-reference")
        .attr("stroke", "#777")
        .attr("stroke-width", 1.5)
        .attr("stroke-opacity", 0.5);

      // Línea de promedio/mediana vertical (FP) - desde eje X hasta intersección
      staticGroup
        .append("line")
        .attr("class", "fp-average-line-vertical")
        .attr("stroke", "#0066cc")
        .attr("stroke-width", 1.5)
        .attr("stroke-dasharray", "5,5")
        .attr("stroke-opacity", 0.6);

      // Línea de promedio/mediana horizontal (FP) - desde eje Y hasta intersección
      staticGroup
        .append("line")
        .attr("class", "fp-average-line-horizontal")
        .attr("stroke", "#0066cc")
        .attr("stroke-width", 1.5)
        .attr("stroke-dasharray", "5,5")
        .attr("stroke-opacity", 0.6);

      // Línea de promedio/mediana horizontal (LLA) - desde eje Y hasta intersección
      staticGroup
        .append("line")
        .attr("class", "lla-average-line-horizontal")
        .attr("stroke", "#8b5cf6")
        .attr("stroke-width", 1.5)
        .attr("stroke-dasharray", "5,5")
        .attr("stroke-opacity", 0.6);

      // Línea de promedio/mediana vertical (LLA) - desde eje X hasta intersección
      staticGroup
        .append("line")
        .attr("class", "lla-average-line-vertical")
        .attr("stroke", "#8b5cf6")
        .attr("stroke-width", 1.5)
        .attr("stroke-dasharray", "5,5")
        .attr("stroke-opacity", 0.6);

      // Etiqueta FP en eje X
      staticGroup
        .append("text")
        .attr("class", "fp-label")
        .attr("fill", "#0066cc")
        .attr("font-size", "11px")
        .attr("font-weight", "600")
        .attr("text-anchor", "middle");

      // Etiqueta LLA en eje Y
      staticGroup
        .append("text")
        .attr("class", "lla-label")
        .attr("fill", "#8b5cf6")
        .attr("font-size", "11px")
        .attr("font-weight", "600")
        .attr("text-anchor", "middle");

      // Eje X (FP - horizontal)
      staticGroup.append("g").attr("class", "x-axis");

      // Eje Y (LLA - vertical)
      staticGroup.append("g").attr("class", "y-axis");

      // Etiquetas de cuadrantes
      const labelGroup = staticGroup
        .append("g")
        .attr("class", "quadrant-labels");

      // Cuadrante superior izquierdo (FP negativo, LLA positivo)
      labelGroup
        .append("text")
        .attr("class", "label-tl-1")
        .attr("fill", "#8b5cf6")
        .attr("font-size", "14px")
        .attr("font-weight", "700")
        .attr("opacity", 0.5)
        .text("mejora LLA");

      labelGroup
        .append("text")
        .attr("class", "label-tl-2")
        .attr("fill", "#0066cc")
        .attr("font-size", "14px")
        .attr("font-weight", "700")
        .attr("opacity", 0.5)
        .text("empeora FP");

      // Cuadrante superior derecho (FP positivo, LLA positivo)
      labelGroup
        .append("text")
        .attr("class", "label-tr-1")
        .attr("fill", "#8b5cf6")
        .attr("font-size", "14px")
        .attr("font-weight", "700")
        .attr("text-anchor", "end")
        .attr("opacity", 0.5)
        .text("mejora LLA");

      labelGroup
        .append("text")
        .attr("class", "label-tr-2")
        .attr("fill", "#0066cc")
        .attr("font-size", "14px")
        .attr("font-weight", "700")
        .attr("text-anchor", "end")
        .attr("opacity", 0.5)
        .text("mejora FP");

      // Cuadrante inferior izquierdo (FP negativo, LLA negativo)
      labelGroup
        .append("text")
        .attr("class", "label-bl-1")
        .attr("fill", "#8b5cf6")
        .attr("font-size", "14px")
        .attr("font-weight", "700")
        .attr("opacity", 0.5)
        .text("empeora LLA");

      labelGroup
        .append("text")
        .attr("class", "label-bl-2")
        .attr("fill", "#0066cc")
        .attr("font-size", "14px")
        .attr("font-weight", "700")
        .attr("opacity", 0.5)
        .text("empeora FP");

      // Cuadrante inferior derecho (FP positivo, LLA negativo)
      labelGroup
        .append("text")
        .attr("class", "label-br-1")
        .attr("fill", "#8b5cf6")
        .attr("font-size", "14px")
        .attr("font-weight", "700")
        .attr("text-anchor", "end")
        .attr("opacity", 0.5)
        .text("empeora LLA");

      labelGroup
        .append("text")
        .attr("class", "label-br-2")
        .attr("fill", "#0066cc")
        .attr("font-size", "14px")
        .attr("font-weight", "700")
        .attr("text-anchor", "end")
        .attr("opacity", 0.5)
        .text("mejora FP");
    }

    // Actualizar posiciones de elementos estáticos
    staticGroup
      .select<SVGLineElement>("line.vertical-reference")
      .attr("x1", x(0))
      .attr("x2", x(0))
      .attr("y1", marginTop)
      .attr("y2", height - marginBottom);

    staticGroup
      .select<SVGLineElement>("line.horizontal-reference")
      .attr("x1", marginLeft)
      .attr("x2", width - marginRight)
      .attr("y1", y(0))
      .attr("y2", y(0));

    // Calcular punto de intersección
    const intersectionX = x(fpAverage);
    const intersectionY = y(llaAverage);

    // Línea vertical FP (desde eje X hasta intersección)
    staticGroup
      .select<SVGLineElement>("line.fp-average-line-vertical")
      .attr("x1", intersectionX)
      .attr("x2", intersectionX)
      .attr("y1", y(0))
      .attr("y2", intersectionY);

    // Línea horizontal FP (desde eje Y hasta intersección) - NO SE USA, eliminamos
    staticGroup
      .select<SVGLineElement>("line.fp-average-line-horizontal")
      .attr("x1", 0)
      .attr("x2", 0)
      .attr("y1", 0)
      .attr("y2", 0)
      .attr("stroke-opacity", 0);

    // Línea horizontal LLA (desde eje vertical x=0 hasta intersección)
    staticGroup
      .select<SVGLineElement>("line.lla-average-line-horizontal")
      .attr("x1", x(0))
      .attr("x2", intersectionX)
      .attr("y1", intersectionY)
      .attr("y2", intersectionY);

    // Línea vertical LLA (desde eje X hasta intersección)
    staticGroup
      .select<SVGLineElement>("line.lla-average-line-vertical")
      .attr("x1", intersectionX)
      .attr("x2", intersectionX)
      .attr("y1", y(0))
      .attr("y2", intersectionY);

    // Actualizar etiqueta FP en eje X
    const fpSign = fpAverage >= 0 ? "+" : "";
    staticGroup
      .select<SVGTextElement>("text.fp-label")
      .attr("x", intersectionX)
      .attr("y", y(0) + 15)
      .text(`FP: ${fpSign}${fpAverage.toFixed(1)}`);

    // Actualizar etiqueta LLA en eje Y vertical (en x=0)
    const llaSign = llaAverage >= 0 ? "+" : "";
    staticGroup
      .select<SVGTextElement>("text.lla-label")
      .attr("x", x(0) - 10)
      .attr("y", intersectionY + 4)
      .attr("text-anchor", "end")
      .text(`LLA: ${llaSign}${llaAverage.toFixed(1)}`);

    // Actualizar eje X
    staticGroup
      .select<SVGGElement>("g.x-axis")
      .attr("transform", `translate(0,${height - marginBottom})`)
      .call(d3.axisBottom(x).ticks(width / 80))
      .call((g) => g.select(".domain").remove())
      .call((g) => {
        g.selectAll("text.x-label").remove();
        g.append("text")
          .attr("class", "x-label")
          .attr("x", width - marginRight)
          .attr("y", 35)
          .attr("fill", "#0066cc")
          .attr("text-anchor", "end")
          .attr("font-weight", "600")
          .attr("font-size", "12px")
          .text("Fuerza Patria →");
      });

    // Actualizar eje Y
    staticGroup
      .select<SVGGElement>("g.y-axis")
      .attr("transform", `translate(${marginLeft},0)`)
      .call(d3.axisLeft(y))
      .call((g) => g.select(".domain").remove())
      .call((g) => {
        g.selectAll("text.y-label").remove();
        g.append("text")
          .attr("class", "y-label")
          .attr("x", 10)
          .attr("y", marginTop - 10)
          .attr("fill", "#8b5cf6")
          .attr("text-anchor", "start")
          .attr("font-weight", "600")
          .attr("font-size", "12px")
          .text("↑ LLA");
      });

    // Actualizar posiciones de etiquetas
    staticGroup
      .select("text.label-tl-1")
      .attr("x", marginLeft + 30)
      .attr("y", marginTop + 30);
    staticGroup
      .select("text.label-tl-2")
      .attr("x", marginLeft + 30)
      .attr("y", marginTop + 50);
    staticGroup
      .select("text.label-tr-1")
      .attr("x", width - marginRight - 30)
      .attr("y", marginTop + 30);
    staticGroup
      .select("text.label-tr-2")
      .attr("x", width - marginRight - 30)
      .attr("y", marginTop + 50);
    staticGroup
      .select("text.label-bl-1")
      .attr("x", marginLeft + 30)
      .attr("y", height - marginBottom - 50);
    staticGroup
      .select("text.label-bl-2")
      .attr("x", marginLeft + 30)
      .attr("y", height - marginBottom - 30);
    staticGroup
      .select("text.label-br-1")
      .attr("x", width - marginRight - 30)
      .attr("y", height - marginBottom - 50);
    staticGroup
      .select("text.label-br-2")
      .attr("x", width - marginRight - 30)
      .attr("y", height - marginBottom - 30);

    // Tooltip (crear solo si no existe)
    let tooltip = d3
      .select(containerRef.current)
      .select<HTMLDivElement>("div.scatter-tooltip");
    if (tooltip.empty()) {
      tooltip = d3
        .select(containerRef.current)
        .append("div")
        .attr("class", "scatter-tooltip")
        .style("position", "absolute")
        .style("visibility", "hidden")
        .style("background-color", "rgba(0, 0, 0, 0.85)")
        .style("color", "white")
        .style("padding", "8px 12px")
        .style("border-radius", "4px")
        .style("font-size", "11px")
        .style("pointer-events", "none")
        .style("z-index", "1000")
        .style("line-height", "1.4");
    }

    // Crear grupo de círculos si no existe
    let circlesGroup = svg.select<SVGGElement>("g.circles-group");
    if (circlesGroup.empty()) {
      circlesGroup = svg.append("g").attr("class", "circles-group");
    }

    // Crear/actualizar puntos usando update pattern
    const circles = circlesGroup
      .selectAll<SVGCircleElement, ScatterDataPoint>("circle")
      .data(scatterData, (d) => d.id);

    // Enter + Update
    circles
      .join(
        (enter) =>
          enter
            .append("circle")
            .attr("cx", (d) => x(d.fp))
            .attr("cy", (d) => y(d.lla))
            .attr("r", 0)
            .attr("fill", (d) => {
              // Si no está visible, gris
              if (!d.isVisible) return "#d1d5db";
              // Color según cuadrante
              if (d.fp >= 0 && d.lla >= 0) return "#8b5cf6"; // Ambos positivos - morado
              if (d.fp >= 0 && d.lla < 0) return "#0066cc"; // FP positivo, LLA negativo - azul
              if (d.fp < 0 && d.lla >= 0) return "#a78bfa"; // FP negativo, LLA positivo - morado claro
              return "#60a5fa"; // Ambos negativos - azul claro
            })
            .attr("fill-opacity", (d) => (d.isVisible ? 0.6 : 0.3))
            .attr("stroke", (d) => {
              // Si no está visible, sin borde
              if (!d.isVisible) return "none";
              if (d.fp >= 0 && d.lla >= 0) return "#7c3aed";
              if (d.fp >= 0 && d.lla < 0) return "#0052a3";
              if (d.fp < 0 && d.lla >= 0) return "#8b5cf6";
              return "#0066cc";
            })
            .attr("stroke-width", (d) => (d.isVisible ? 1.5 : 0))
            .call((enter) =>
              enter
                .transition()
                .duration(500)
                .attr("r", (d) => radiusScale(d.totalVotes)),
            ),
        (update) =>
          update.call((update) =>
            update
              .transition()
              .duration(500)
              .attr("cx", (d) => x(d.fp))
              .attr("cy", (d) => y(d.lla))
              .attr("r", (d) => radiusScale(d.totalVotes))
              .attr("fill", (d) => {
                // Si no está visible, gris
                if (!d.isVisible) return "#d1d5db";
                // Color según cuadrante
                if (d.fp >= 0 && d.lla >= 0) return "#8b5cf6";
                if (d.fp >= 0 && d.lla < 0) return "#0066cc";
                if (d.fp < 0 && d.lla >= 0) return "#a78bfa";
                return "#60a5fa";
              })
              .attr("fill-opacity", (d) => (d.isVisible ? 0.6 : 0.3))
              .attr("stroke", (d) => {
                // Si no está visible, sin borde
                if (!d.isVisible) return "none";
                if (d.fp >= 0 && d.lla >= 0) return "#7c3aed";
                if (d.fp >= 0 && d.lla < 0) return "#0052a3";
                if (d.fp < 0 && d.lla >= 0) return "#8b5cf6";
                return "#0066cc";
              })
              .attr("stroke-width", (d) => (d.isVisible ? 1.5 : 0)),
          ),
        (exit) =>
          exit.call((exit) =>
            exit.transition().duration(300).attr("r", 0).remove(),
          ),
      )
      .on("mouseover", function (event, d) {
        // Solo interacción en puntos visibles
        if (!d.isVisible) return;

        const currentRadius = radiusScale(d.totalVotes);
        d3.select(this)
          .transition()
          .duration(200)
          .attr("r", currentRadius * 1.5)
          .attr("stroke-width", 2.5);

        tooltip
          .style("visibility", "visible")
          .html(
            `<strong>${d.distrito} - ${d.circuito}</strong><br/>` +
              `Mesa ${d.mesaNumber} (${d.totalVotes} votos)<br/>` +
              `<span style="color: #60a5fa;">FP: ${d.fp >= 0 ? "+" : ""}${d.fp.toFixed(1)}%</span><br/>` +
              `<span style="color: #c4b5fd;">LLA: ${d.lla >= 0 ? "+" : ""}${d.lla.toFixed(1)}%</span>`,
          );
      })
      .on("mousemove", function (event) {
        tooltip
          .style(
            "top",
            `${event.pageY - containerRef.current!.getBoundingClientRect().top - 10}px`,
          )
          .style(
            "left",
            `${event.pageX - containerRef.current!.getBoundingClientRect().left + 15}px`,
          );
      })
      .on("mouseout", function (event, d) {
        // Solo interacción en puntos visibles
        if (!d.isVisible) return;

        d3.select(this)
          .transition()
          .duration(200)
          .attr("r", radiusScale(d.totalVotes))
          .attr("stroke-width", 1.5);

        tooltip.style("visibility", "hidden");
      });

    // Cleanup: no hacemos nada aquí, dejamos que los elementos persistan
    // Se limpiarán automáticamente cuando el componente se desmonte
  }, [data, filteredIds, llaAverage, fpAverage]);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full"
      style={{ minHeight: "400px" }}
    >
      <svg ref={svgRef} className="h-full w-full" />
    </div>
  );
}
